import React, { useCallback, useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
interface ScannedProduct {
  title: string;
  editionLabel: string;
  priceNumber: number;
  priceText: string;
  thumbnailUrl: string;
}
interface Notification {
  id: number;
  name: string;
  city: string;
  product: ScannedProduct;
}
const NAMES = ['Carlos M.', 'María G.', 'José R.', 'Ana L.', 'Miguel S.', 'Rosa P.', 'David H.', 'Carmen T.', 'Luis F.', 'Patricia V.', 'Roberto A.', 'Sandra K.', 'Juan C.', 'Elena D.', 'Fernando B.', 'Jessica W.', 'Michael T.', 'Sarah J.', 'James P.', 'Jennifer L.', 'Pedro N.', 'Claudia R.', 'Andrés M.', 'Lucía H.', 'Diego S.', 'Gabriela F.', 'Alejandro V.', 'Isabella C.', 'Daniel O.', 'Valentina B.'];
const CITIES = ['Hyattsville, MD', 'New Carrollton, MD', 'College Park, MD', 'Silver Spring, MD', 'Bowie, MD', 'Laurel, MD', 'Greenbelt, MD', 'Riverdale, MD', 'Bladensburg, MD', 'Landover, MD', 'Washington, DC', 'Arlington, VA', 'Alexandria, VA', 'Falls Church, VA', 'Takoma Park, MD', 'Adelphi, MD', 'Capitol Heights, MD', 'Temple Hills, MD', 'Suitland, MD', 'Cheverly, MD', 'Mount Rainier, MD', 'Brentwood, MD'];
const FALLBACK_PRODUCTS: ScannedProduct[] = [{
  title: 'Estimado Gratis',
  editionLabel: 'Roofing / Techos',
  priceNumber: 0,
  priceText: 'GRATIS',
  thumbnailUrl: ''
}, {
  title: 'Estimado Gratis',
  editionLabel: 'Fences / Cercas',
  priceNumber: 0,
  priceText: 'GRATIS',
  thumbnailUrl: ''
}, {
  title: 'Estimado Gratis',
  editionLabel: 'Painting / Pintura',
  priceNumber: 0,
  priceText: 'GRATIS',
  thumbnailUrl: ''
}, {
  title: 'Estimado Gratis',
  editionLabel: 'Drywall',
  priceNumber: 0,
  priceText: 'GRATIS',
  thumbnailUrl: ''
}, {
  title: 'Estimado Gratis',
  editionLabel: 'Floors / Pisos',
  priceNumber: 0,
  priceText: 'GRATIS',
  thumbnailUrl: ''
}, {
  title: 'Estimado Gratis',
  editionLabel: 'Masonry / Albañilería',
  priceNumber: 0,
  priceText: 'GRATIS',
  thumbnailUrl: ''
}, {
  title: 'Estimado Gratis',
  editionLabel: 'Power Washing',
  priceNumber: 0,
  priceText: 'GRATIS',
  thumbnailUrl: ''
}, {
  title: 'Estimado Gratis',
  editionLabel: 'Concreto',
  priceNumber: 0,
  priceText: 'GRATIS',
  thumbnailUrl: ''
}, {
  title: 'Estimado Gratis',
  editionLabel: 'Windows / Ventanas',
  priceNumber: 0,
  priceText: 'GRATIS',
  thumbnailUrl: ''
}, {
  title: 'Estimado Gratis',
  editionLabel: 'Patio Restauración',
  priceNumber: 0,
  priceText: 'GRATIS',
  thumbnailUrl: ''
}];
function scanPageProducts(): ScannedProduct[] {
  const products: ScannedProduct[] = [];
  try {
    // Try JSON-LD structured data
    const jsonLdScripts = document.querySelectorAll('script[type="application/ld+json"]');
    jsonLdScripts.forEach(script => {
      try {
        const data = JSON.parse(script.textContent || '');
        if (data['@type'] === 'Product' || data['@type'] === 'Service') {
          const offer = data.offers || {};
          products.push({
            title: data.name || 'Servicio',
            editionLabel: data.category || '',
            priceNumber: parseFloat(offer.price) || 0,
            priceText: offer.price ? `$${offer.price}` : 'GRATIS',
            thumbnailUrl: data.image || ''
          });
        }
      } catch {}
    });
    if (products.length > 0) return products;
    // Scan for price-like patterns on the page
    const priceRegex = /\$\s?\d+(\.\d{2})?/g;
    const allText = document.body.innerText || '';
    const priceMatches = allText.match(priceRegex);
    if (priceMatches && priceMatches.length > 0) {
      // Try to find product cards near prices
      const elements = document.querySelectorAll('[class*="product"], [class*="service"], [class*="card"], [class*="pricing"]');
      elements.forEach(el => {
        const text = el.textContent || '';
        const price = text.match(priceRegex);
        if (price) {
          const img = el.querySelector('img');
          const heading = el.querySelector('h1, h2, h3, h4, h5, h6');
          products.push({
            title: heading?.textContent?.trim() || 'Servicio',
            editionLabel: '',
            priceNumber: parseFloat(price[0].replace(/[^0-9.]/g, '')) || 0,
            priceText: price[0],
            thumbnailUrl: img?.src || ''
          });
        }
      });
    }
  } catch {}
  return products;
}
function getRandomItem<T>(arr: T[]): T {
  return arr[Math.floor(Math.random() * arr.length)];
}
function getRandomInterval(): number {
  return Math.floor(Math.random() * 7000) + 8000; // 8-15 seconds
}
export function RecentPurchaseToast() {
  const [notification, setNotification] = useState<Notification | null>(null);
  const [products, setProducts] = useState<ScannedProduct[]>([]);
  const nameHistory = useRef<string[]>([]);
  const idCounter = useRef(0);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const prefersReducedMotion = typeof window !== 'undefined' ? window.matchMedia('(prefers-reduced-motion: reduce)').matches : false;
  useEffect(() => {
    const scanned = scanPageProducts();
    setProducts(scanned.length > 0 ? scanned : FALLBACK_PRODUCTS);
  }, []);
  const pickName = useCallback((): string => {
    const available = NAMES.filter(n => !nameHistory.current.includes(n));
    const pool = available.length > 0 ? available : NAMES;
    const name = getRandomItem(pool);
    nameHistory.current.push(name);
    if (nameHistory.current.length > 10) {
      nameHistory.current.shift();
    }
    return name;
  }, []);
  const showNotification = useCallback(() => {
    if (products.length === 0) return;
    const name = pickName();
    const city = getRandomItem(CITIES);
    const product = getRandomItem(products);
    idCounter.current += 1;
    setNotification({
      id: idCounter.current,
      name,
      city,
      product
    });
    // Auto-hide after 4 seconds
    setTimeout(() => {
      setNotification(null);
    }, 4000);
    // Schedule next
    timerRef.current = setTimeout(showNotification, getRandomInterval());
  }, [products, pickName]);
  useEffect(() => {
    if (products.length === 0) return;
    // First notification after 3 seconds
    timerRef.current = setTimeout(showNotification, 3000);
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [products, showNotification]);
  const variants = prefersReducedMotion ? {
    initial: {
      opacity: 0
    },
    animate: {
      opacity: 1
    },
    exit: {
      opacity: 0
    }
  } : {
    initial: {
      opacity: 0,
      y: 60,
      scale: 0.95
    },
    animate: {
      opacity: 1,
      y: 0,
      scale: 1
    },
    exit: {
      opacity: 0,
      y: 40,
      scale: 0.95
    }
  };
  return <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[100] pointer-events-none" data-id="element-225">
      <AnimatePresence mode="wait" data-id="element-226">
        {notification && <motion.div key={notification.id} initial={variants.initial} animate={variants.animate} exit={variants.exit} transition={{
        duration: 0.4,
        ease: 'easeOut'
      }} className="pointer-events-auto bg-white rounded-xl shadow-2xl border border-gray-200 px-4 py-3 flex items-center gap-3 max-w-sm sm:max-w-md" data-id="element-227">
            {/* Service Icon */}
            <div className="flex-shrink-0 w-12 h-12 bg-brand-green/10 rounded-lg flex items-center justify-center" data-id="element-228">
              <svg className="w-7 h-7 text-brand-green" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} data-id="element-229">
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" data-id="element-230" />
              </svg>
            </div>

            {/* Content */}
            <div className="flex-1 min-w-0" data-id="element-231">
              <p className="text-sm text-gray-900 font-semibold leading-tight truncate" data-id="element-232">
                {notification.name}{' '}
                <span className="font-normal text-gray-500" data-id="element-233">en</span>{' '}
                {notification.city}
              </p>
              <p className="text-xs text-gray-600 mt-0.5 leading-tight" data-id="element-234">
                solicitó{' '}
                <span className="font-bold text-brand-green" data-id="element-235">
                  {notification.product.title}
                </span>
                {notification.product.editionLabel && <span className="text-gray-500" data-id="element-236">
                    {' '}
                    — {notification.product.editionLabel}
                  </span>}
              </p>
              <p className="text-[10px] text-gray-400 mt-1" data-id="element-237">
                Hace {Math.floor(Math.random() * 5) + 1} minutos
              </p>
            </div>

            {/* Badge */}
            <div className="flex-shrink-0" data-id="element-238">
              <span className="inline-block bg-brand-green text-white text-xs font-bold px-2 py-1 rounded-full uppercase tracking-wider" data-id="element-239">
                {notification.product.priceText}
              </span>
            </div>
          </motion.div>}
      </AnimatePresence>
    </div>;
}